import {LitElement, html, css} from "../libs/lit.dist.js";
import {getGlobalStyleSheets} from "./abstract-element.js";

/**
 * A custom element for displaying icons using different icon collections.
 * `FaIcon` extends LitElement and provides flexibility to render icons from
 * various icon collections including FontAwesome, Bootstrap icons, and custom icon collections.
 *
 * Key Features:
 * - Supports multiple icon collections such as FontAwesome, Bootstrap, and custom collections.
 * - Offers customization for icon type (e.g., regular, solid, light, dual tone).
 * - Dynamically computes and applies CSS classes for proper rendering of icons.
 * - Encapsulates styles and ensures ease of integration with various projects.
 *
 * Properties:
 * - `icon` (String): The name of the icon to be displayed. Automatically prefixes the icon name based on the specified collection.
 * - `type` (String): Specifies the type of icon to use within the collection. For FontAwesome, valid types include "regular", "solid", "light", and "dual tone".
 * - `collection` (String): The icon collection to be used. Supported options include:
 *    - "bi": Bootstrap Icons
 *    - "pk": Custom icon name prefix "pk-icon-"
 *    - Default: FontAwesome with applicable subtypes.
 *
 * CSS:
 * - Defines base styles to center-align the icon and render them inline.
 * - Additional styles are applied through dynamically calculated class names.
 */
export class FaIcon extends LitElement {

    static get styles() {
        return [
            getGlobalStyleSheets(),
            css`
                :host {
                    text-align: center;
                    display: inline-block;
                }
                i {
                    display: inline-block;
                }
            `,
        ];
    }

    static properties = {
        icon: {type: String},
        type: {type: String},
        collection: {type: String},
        fixedWidth: {type: Boolean},
    }

    get #innerCssClasses() {
        const classes = [];
        let prefix;
        switch (this.collection) {
            case 'bi':
                prefix = 'bi-';
                classes.push('bi');
                break;

            case 'pk':
                prefix = 'pk-icon-';
                classes.push('pk');
                break;

            default:
                prefix = 'fa-';
                switch (this.type) {
                    case 'regular':
                    case 'far':
                        classes.push('far');
                        break;

                    case 'light':
                    case 'fal':
                        classes.push('fal');
                        break;

                    case 'dual':
                    case 'dual_tone':
                    case 'dualTone':
                    case 'fad':
                        classes.push('fad');
                        break;

                    case 'solid':
                    case 'fas':
                        classes.push('fas');
                        break;

                    default:
                        classes.push('fa');
                }
                break;
        }

        if (this.fixedWidth) {
            classes.push('fa-fw');
        }

        let icon = String(this.icon).trim();
        if (icon.indexOf(prefix) !== 0) {
            icon = prefix + this.icon;
        }
        if (icon) {
            classes.push(icon);
        }
        return classes;
    }

    render() {
        return html`<i class="${this.#innerCssClasses.join(' ')}"></i>`;
    }
}

if (!customElements.get('fa-icon')) {
    customElements.define('fa-icon', FaIcon);
}
